import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { isEmpty, has as LodashHas } from 'lodash-es';

type HandlePushCallback = (args: { data: any; key: string }) => void;
type HandleRemoveCallback = (args: { key: string; value: string }) => void;
type HasCallback = (args: { key: string; value?: string }) => boolean;

interface DataLayerContextType {
  handlePushAndRemove: (args: { data: any; key: string }) => void;
}

const DataLayerContext = createContext<DataLayerContextType>({
  handlePushAndRemove: () => {},
});

export const useDataLayer = () => {
  const context = useContext(DataLayerContext);

  if (context === undefined) {
    throw new Error('useDataLayer must be used within a DataLayerContext');
  }

  return context;
};

const DataLayerProvider = ({ children }: { children?: React.ReactNode }) => {
  const [isDataLayerReady, setIsDataLayerReady] = useState<boolean>(false);
  const [dataLayer, setDataLayer] = useState<any[]>([]);

  const waitUntilDataLayerReady = useCallback(() => {
    if (!isDataLayerReady) {
      const intervalId = setInterval(() => {
        const gtmLoaded = (window as any)?.google_tag_manager?.dataLayer
          ?.gtmLoad;

        setIsDataLayerReady(gtmLoaded);
        clearInterval(intervalId);
      }, 1000);
    }
  }, [isDataLayerReady]);

  const has = useCallback<HasCallback>(({ key, value }) => {
    if (isEmpty(value))
      return window?.dataLayer?.some((item) => LodashHas(item, key));

    return window?.dataLayer?.some((item) => item?.[key] === value);
  }, []);

  const handlePush = useCallback<HandlePushCallback>(
    ({ data, key }) => {
      if (isEmpty(data[key])) return;
      if (has({ key, value: data[key] })) return;

      window?.dataLayer?.push({
        ...data,
      });
    },
    [has],
  );

  const handleRemove = useCallback<HandleRemoveCallback>(
    ({ key, value }) => {
      if (!has({ key, value })) return;

      const index = window?.dataLayer?.findIndex(
        (item) => item?.[key] === value,
      );

      window.dataLayer.splice(index, 1);
    },
    [has],
  );

  const handlePushAndRemove = useCallback<HandlePushCallback>(
    ({ data, key }) => {
      setDataLayer([...dataLayer, { data, key }]);
    },
    [dataLayer],
  );

  useEffect(() => {
    window.addEventListener('load', waitUntilDataLayerReady);

    return () => window.removeEventListener('load', waitUntilDataLayerReady);
  }, [waitUntilDataLayerReady]);

  useEffect(() => {
    if (dataLayer.length > 0) {
      dataLayer.forEach((dataLayerObj) => {
        handlePush({ ...dataLayerObj });

        setTimeout(
          () =>
            handleRemove({
              key: dataLayerObj.key,
              value: dataLayerObj.data[dataLayerObj.key],
            }),
          2000,
        );
      });

      setDataLayer([]);
    }
  }, [dataLayer, handlePush, handleRemove, isDataLayerReady]);

  return (
    <DataLayerContext.Provider value={{ handlePushAndRemove }}>
      {children}
    </DataLayerContext.Provider>
  );
};

export default DataLayerProvider;
